import { useCallback, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import "./stand-subscription-center.css";

type Plan = {
  id: string; name: string; description: string | null; price_cents: number;
  currency_code: string; trial_days: number | null; features: Record<string, boolean>;
  limits: Record<string, number | null>;
};
type Subscription = {
  id: string; effective_status: string; auto_renew: boolean; cancel_at_period_end: boolean;
  trial_ends_at: string | null; current_period_end: string | null;
  terms_snapshot: { name?: string; price_cents?: number; currency_code?: string };
};
type Usage = { key: string; used: number; limit: number | null; percent: number };

const statusLabels: Record<string, string> = {
  active: "Ativo", trialing: "Degustação ativa", read_only: "Apenas leitura",
  suspended: "Suspenso", pending: "Pendente", cancelled: "Cancelado",
};
const metricLabels: Record<string, string> = {
  users: "Utilizadores", vehicles: "Viaturas", documents: "Documentos",
  ai_invoice_reads: "Leituras inteligentes", generated_documents: "Documentos gerados",
  active_listings: "Anúncios ativos",
};
const featureLabels: Record<string, string> = {
  crm: "CRM", stock: "Stock", tasks: "Tarefas", website: "Site", documents: "Documentos",
  chat: "Chat", finance: "Financeiro", external_ai: "IA documental", marketplaces: "Marketplaces",
  multi_branch: "Filiais",
};
const money = (cents: number, currency = "EUR") => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency,
}).format(cents / 100);

export function StandSubscriptionCenter({ organizationId, canManage }: { organizationId: string; canManage: boolean }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(async () => {
    await supabase.rpc("synchronize_organization_subscription_access", { p_organization_id: organizationId });
    const [catalog, current, measured] = await Promise.all([
      supabase.rpc("commercial_plan_catalog"),
      supabase.from("organization_subscription_access")
        .select("id,effective_status,auto_renew,cancel_at_period_end,trial_ends_at,current_period_end,terms_snapshot")
        .eq("organization_id", organizationId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.rpc("subscription_usage_overview", { p_organization_id: organizationId }),
    ]);
    if (catalog.error) throw catalog.error;
    if (current.error) throw current.error;
    if (measured.error) throw measured.error;
    const available = (catalog.data ?? []) as Plan[];
    setPlans(available);
    setSelectedPlan((value) => value || available[0]?.id || "");
    setSubscription(current.data as Subscription | null);
    setUsage((measured.data ?? []) as Usage[]);
  }, [organizationId]);

  useEffect(() => { void load().catch((error: Error) => setMessage({ tone: "error", text: error.message })); }, [load]);

  const execute = async (operation: () => Promise<{ error: { message: string } | null }>, success: string) => {
    setBusy(true); setMessage(null);
    const result = await operation();
    setBusy(false);
    if (result.error) { setMessage({ tone: "error", text: result.error.message }); return; }
    setCheckoutId(null);
    setMessage({ tone: "ok", text: success });
    await load();
  };

  const startCheckout = async () => {
    setBusy(true); setMessage(null);
    const { data, error } = await supabase.rpc("start_test_subscription_checkout", {
      p_organization_id: organizationId, p_plan_id: selectedPlan, p_auto_renew: true,
    });
    setBusy(false);
    if (error) { setMessage({ tone: "error", text: error.message }); return; }
    setCheckoutId(data.id);
  };

  const currentName = subscription?.terms_snapshot?.name ?? "Sem plano ativo";
  return <div className="stand-subscription-center">
    <header className="subscription-current">
      <div><span>Subscrição atual</span><h4>{currentName}</h4>
        <p>{subscription ? statusLabels[subscription.effective_status] : "Escolha um plano para começar."}</p></div>
      {subscription && <div className="subscription-current-state"><strong>{statusLabels[subscription.effective_status]}</strong>
        <small>{subscription.trial_ends_at || subscription.current_period_end ? `Até ${new Date(subscription.trial_ends_at ?? subscription.current_period_end!).toLocaleDateString("pt-PT")}` : "Sem expiração"}</small></div>}
    </header>

    {message && <button type="button" className={`subscription-message ${message.tone}`} onClick={() => setMessage(null)}>{message.text} ×</button>}

    {usage.length > 0 && <section className="subscription-usage"><div><h4>Utilização do plano</h4><p>Os avisos surgem aos 80% e 100%. Não existem cobranças automáticas de excedentes.</p></div>
      <div className="usage-grid">{usage.map((item) => <article key={item.key} className={item.percent >= 100 ? "limit" : item.percent >= 80 ? "warning" : ""}>
        <span>{metricLabels[item.key] ?? item.key}</span><strong>{item.used}{item.limit === null ? "" : ` / ${item.limit}`}</strong>
        {item.limit !== null && <div><i style={{ width: `${Math.min(item.percent, 100)}%` }} /></div>}
        {item.percent >= 100 && <small>Limite atingido</small>}{item.percent >= 80 && item.percent < 100 && <small>A aproximar-se do limite</small>}
      </article>)}</div>
    </section>}

    <section><div className="subscription-section-title"><h4>Planos disponíveis</h4><p>Todos incluem 7 dias de degustação. O plano técnico de Testes não aparece neste catálogo.</p></div>
      <div className="commercial-plan-grid">{plans.map((plan) => <button type="button" key={plan.id} className={selectedPlan === plan.id ? "selected" : ""} onClick={() => setSelectedPlan(plan.id)}>
        <span>{plan.trial_days ?? 7} dias de degustação</span><h4>{plan.name}</h4><p>{plan.description}</p>
        <strong>{money(plan.price_cents, plan.currency_code)} <small>/ mês</small></strong>
        <ul>{Object.entries(plan.features).filter(([, enabled]) => enabled).slice(0, 7).map(([key]) => <li key={key}>{featureLabels[key] ?? key}</li>)}</ul>
      </button>)}</div>
    </section>

    {canManage ? <section className="subscription-self-service"><h4>Gerir acesso</h4>
      {!checkoutId ? <div className="subscription-buttons">
        <button type="button" className="secondary-button" disabled={busy || !selectedPlan || subscription?.effective_status === "active" || subscription?.effective_status === "trialing"}
          onClick={() => void execute(async () => await supabase.rpc("start_organization_trial", { p_organization_id: organizationId, p_plan_id: selectedPlan }), "Degustação iniciada. O acesso está ativo durante 7 dias.")}>Iniciar degustação</button>
        <button type="button" className="primary-button compact" disabled={busy || !selectedPlan} onClick={() => void startCheckout()}>Contratar plano</button>
        {subscription?.effective_status === "active" && <button type="button" className="secondary-button" disabled={busy}
          onClick={() => void execute(async () => await supabase.rpc("set_my_subscription_renewal", { p_organization_id: organizationId, p_auto_renew: !subscription.auto_renew }), subscription.auto_renew ? "Cancelamento agendado para o fim do período." : "Renovação automática ativada.")}>{subscription.auto_renew ? "Cancelar no fim do período" : "Ativar renovação"}</button>}
      </div> : <article className="test-checkout" role="dialog" aria-label="Checkout de Testes">
        <div><span>Checkout seguro · ambiente de Testes</span><h4>Confirmar contratação</h4><p>Nenhum cartão real será cobrado. Esta confirmação simula o pagamento e ativa imediatamente o plano escolhido.</p></div>
        <div><button type="button" className="secondary-button" onClick={() => setCheckoutId(null)}>Cancelar</button>
          <button type="button" className="primary-button compact" disabled={busy} onClick={() => void execute(async () => await supabase.rpc("complete_test_subscription_checkout", { p_checkout_id: checkoutId }), "Pagamento de Testes confirmado. O plano está ativo.")}>Confirmar pagamento de Testes</button></div>
      </article>}
    </section> : <p className="settings-readonly">Apenas o Master do stand pode alterar a subscrição.</p>}
  </div>;
}
